/**
 * "Create enquiry from this mail" — one line, not a form.
 *
 * ---- Why this replaced a whole panel ----
 *
 * The old `NewEnquiryPanel` asked for customer, enquiry type, delivery location and purchaser, and
 * showed the lines for triage. Then `/rfq/:id` showed the same header fields, editable again, and
 * the same lines. The work was done twice, and the RFQ header card is a strict superset of what
 * the panel could edit — so nothing it collected was un-fixable downstream.
 *
 * What is left here is the irreducible part: `create_enquiry` will not create a document without
 * `customer` AND `delivery_location` (measured — `MandatoryError` names both). So we guess both,
 * show the guesses, and write only when the purchaser presses Create.
 *
 * ---- Company (2026-08-08), the third required field, and the only one we refuse to guess ----
 *
 * `create_enquiry` accepts `company` — `Q7` said it did not; it was wrong, and the collection's own
 * documented body has it (see `NewEnquiryHeader.company` for the probe). Bosun runs SEVEN legal
 * entities across four currencies, and the enquiry is the root every RFQ, supplier quotation,
 * comparison and customer quotation inherits from — so this is the one place the answer can be
 * recorded once and not be disagreed about later.
 *
 * The server does not require it (`reqd: 0`) and this bar does. That is deliberate: an enquiry
 * created without one has its RFQs stamped with `company.ts`'s fallback, and a supplier-facing
 * document under the wrong entity is indistinguishable from a correct one. Unlike customer and
 * delivery location, nothing in the mail can be read to guess it, so there is no guess to show —
 * only a pick to make. It is remembered for the session (`setCompany`), so the second enquiry of
 * the day costs no clicks, and `SettingsPage` sets the same default up front.
 *
 * ---- Why a confirmation step at all ----
 *
 * A wrong customer guess becomes a real record attributed to the wrong company, and customer
 * identity is the field this migration has most often seen keyed wrongly. Nothing is written until
 * Create is pressed. The amber flagging on the RFQ header card is the second net, for a guess that
 * looked plausible for the three seconds this bar was on screen.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui'
import { LinkCombo } from '../../components/LinkPicker'
import { useToast } from '../../components/Toast'
import { useExtractFromMail } from '../../lib/api/useMailExtraction'
import { useCreateEnquiry } from '../../lib/api/useEnquiryMutations'
import { prefetchItemMatches } from '../../lib/api/useItems'
import { setItemScope } from '../../lib/api/itemScope'
import { useDeliveryLocationPicker } from '../../lib/api/useEnquiryFromMail'
import { useCustomerSearch } from '../../lib/api/useCustomerRecord'
import { setCompany, useSessionCompany } from '../../lib/api/company'
import { useCompanies, useCompanyPicker, useCurrentUser, useDeliveryLocations, useOwnCompanies, useDeliveryLocationPriceLists } from '../../lib/api/useMasters'
import { filterOutOwnCompanies } from '../../lib/ownCompanies'
import { pickDeliveryLocation } from '../../lib/deliveryLocation'
import { htmlToText } from '../../lib/mail'
import { canSplit, draftsForMode, groupLinesBySheet, toCreateLine, type SheetMode } from './createEnquiryLines'
import { Badge } from '../../components/Badge'
import { ReadBodyModal } from './ReadBodyModal'
import { extractLinesFromBody, toExtractedLine } from '../../lib/mailBodyExtract'
import { fileLinksIn } from '../../lib/mailFileLinks'
import type { SheetItem } from '../../lib/sheetExtract'
import type { EnquirySummary } from '../../lib/types'
import { useEnquiryTypes } from '../../lib/api/useEnquiryTypes'
import { runSerially } from '../../lib/api/serialWrites'
import type { ExtractedLine } from '../../lib/api/useMailExtraction'
import type { MailMessage, NewEnquiryItem } from '../../lib/types'

export function CreateEnquiryBar({
  mail,
  alreadyProcessed = [],
}: {
  mail: MailMessage
  /**
   * Enquiries already created from THIS mail (`lib/mailProcessed.ts`). Empty by default so the
   * component works standalone; when populated it warns before a duplicate is made.
   */
  alreadyProcessed?: readonly EnquirySummary[]
}) {
  const navigate = useNavigate()
  const toast = useToast()
  // NOT `useSession().user` — that is the 'Akhil'/'Priya'/'Manoj' stub, and the server answers
  // `User 'Akhil' not found`. See `useCurrentUser`'s header.
  const currentUser = useCurrentUser()

  const [started, setStarted] = useState(false)
  const [expandedByUser, setExpandedByUser] = useState<boolean | null>(null)
  const [location, setLocation] = useState('')

  const ownCompanies = useOwnCompanies()
  const locationMaster = useDeliveryLocations()

  const extract = useExtractFromMail()

  const matches = extract.phase.status === 'succeeded' ? extract.phase.result.customer_matches : []

  /**
   * Recomputed, NOT captured in `onSuccess`.
   *
   * The guard used to run once, against whatever `/api/resource/Company` had returned at that
   * instant — and `filterOutOwnCompanies` fails open on an empty list by design. A pre-warmed
   * extraction (feature `B`'s whole point) resolves in milliseconds, routinely before the Company
   * master arrives, so the guard silently did nothing and our own letterhead was offered as a
   * confident guess. A `useMemo` self-corrects the moment the master lands, the same way
   * `guessedLocation` below already did.
   */
  const candidates = useMemo(
    () => filterOutOwnCompanies(matches, ownCompanies.data ?? []),
    [matches, ownCompanies.data],
  )
  /** Candidates existed but every one was us — say so IN the bar. A toast fades; this must not. */
  const allOurs = matches.length > 0 && candidates.length === 0 && (ownCompanies.data?.length ?? 0) > 0

  /** The guess, until the purchaser picks something. `chosen` is their explicit choice. */
  const [chosen, setChosen] = useState('')
  const customer = chosen || (candidates[0] ?? '')

  const guessedLocation = useMemo(() => {
    const text = `${mail.subject}\n${htmlToText(mail.body_html)}`
    return pickDeliveryLocation(text, locationMaster.data ?? [])
  }, [mail.subject, mail.body_html, locationMaster.data])

  const effectiveLocation = location || guessedLocation.location

  /**
   * The company, in order of preference: this bar's own pick, then the session default, then — only
   * if the ERP holds exactly ONE company — that one, since there is then nothing to choose between
   * and asking would be theatre. Seven companies live, so in practice the purchaser picks.
   *
   * Never `FALLBACK_COMPANY`: see `getSessionCompany`'s header for why a create path must not
   * pre-fill a guess.
   */
  const [companyChoice, setCompanyChoice] = useState('')
  const companyMaster = useCompanies()
  const onlyCompany = companyMaster.data?.length === 1 ? companyMaster.data[0].name : ''
  const sessionCompany = useSessionCompany()
  const company = companyChoice || sessionCompany || onlyCompany
  // Customers of the company being picked here — `get_customers` needs it (Akhil, 5d).
  const useScopedCustomers = (search: string) => useCustomerSearch(search, company)

  const chooseCompany = (next: string) => {
    setCompanyChoice(next)
    // Remembered for the rest of the session, so the next enquiry starts filled in.
    setCompany(next)
  }

  /**
   * Lines the purchaser reviewed and accepted, held so the NORMAL bar can create them.
   *
   * Deliberately not created on confirm. The failure branch has no customer or company picker —
   * it returns before the bar renders — so creating from inside the review dialog sent whatever
   * `customer` happened to be, which for a mail whose sender matched no customer is `''`, and the
   * server refused it ("Value missing for Enquiry: Customer"). Accepting the lines now falls
   * THROUGH to the normal bar, which has the pickers, the company, the purchaser, the split
   * choice and the disabled-until-valid Create button that every other source already goes
   * through.
   */
  const [acceptedBodyLines, setAcceptedBodyLines] = useState<ExtractedLine[] | null>(null)

  /*
    Anything Create needs and does not have means they have to act, so open the pickers for them.
    Their own click (`expandedByUser`) always wins over this.

    `acceptedBodyLines` counts as a read mail here. Without it, accepting lines from an
    unstructured body dropped the purchaser onto a collapsed bar with no customer set and no
    visible way to set one — the state behind "Value missing for Enquiry: Customer".
  */
  const hasReadLines = extract.phase.status === 'succeeded' || acceptedBodyLines !== null
  const autoExpand = hasReadLines && (customer === '' || company === '')
  const expanded = expandedByUser ?? autoExpand

  /*
    ---- Reading an unstructured body ----

    `readMailItems` handles the shapes that carry their own structure — an HTML table, a numbered
    list with units. What defeats it is prose: "a couple of drums of hydraulic oil and 4 boxes of
    gloves". That is not a parser gap to close with more rules; every new rule fails on the next
    customer's phrasing. So the model reads it and the purchaser reviews the result
    (`ReadBodyModal`), which is the only path into an enquiry that is not deterministic and the
    only one with a review step.

    Held in local state, not a mutation: nothing is written until the reviewed lines are confirmed.
  */
  /*
    Files the mail LINKS to rather than attaches — see `mailFileLinks.ts` for how common that is
    and why they are offered rather than downloaded.
  */
  const fileLinks = useMemo(() => fileLinksIn(mail.body_html ?? ''), [mail.body_html])

  const [readingBody, setReadingBody] = useState(false)
  const [bodyLines, setBodyLines] = useState<SheetItem[] | null>(null)
  const [bodyPending, setBodyPending] = useState(false)
  const [bodyError, setBodyError] = useState<string | null>(null)

  const readBody = async () => {
    setReadingBody(true)
    setBodyPending(true)
    setBodyError(null)
    setBodyLines(null)
    try {
      setBodyLines(await extractLinesFromBody(`${mail.subject}\n${htmlToText(mail.body_html)}`))
    } catch (error) {
      setBodyError(error instanceof Error ? error.message : 'The mail could not be read.')
    } finally {
      setBodyPending(false)
    }
  }

  /*
    No `onSuccess` navigation here, deliberately.

    A multi-sheet split creates SEVERAL enquiries, and a per-create navigation fired once per
    enquiry — landing the purchaser on whichever happened to finish last, after two other
    navigations they never saw. Both branches below now report and navigate once, when they know
    how many were actually made.
  */
  const priceLists = useDeliveryLocationPriceLists()
  const create = useCreateEnquiry({
    onError: (error) => toast(error.message),
  })

  // EVERY line, matched or not. `create_enquiry` accepts `item_code: null` with `raw_text`
  // (verified live), so an unmatched line now reaches the builder for triage instead of being
  // dropped here as it was before. `toCreateLine` also handles the one case that is NOT
  // accepted — a null `uom`, which is mandatory — see `createEnquiryLines.ts`.
  /*
    Reviewed body lines take precedence when there are any: they exist only when the deterministic
    readers failed, so the two can never both be present for one mail.
  */
  const sourceItems: ExtractedLine[] =
    acceptedBodyLines ?? (extract.phase.status === 'succeeded' ? extract.phase.result.items : [])
  const lines: NewEnquiryItem[] = sourceItems.map(toCreateLine)

  /*
    What the readers actually managed, and what they could not.

    `describeRead` has always produced this — "Read 12 lines from the spreadsheet … Not read:
    MECHANICAL ITEMS.xlsx (no item table on any of its 4 sheets)" — and it was stored on the result
    as `notes` and rendered NOWHERE.

    That is the whole of the reported symptom: a mail with three spreadsheets where two yield no
    items produces ONE draft, so the per-sheet/combined/one-sheet choice never appears (it needs two
    to be a choice), and nothing on screen said two files had been skipped. The purchaser sees a
    short order and no explanation.
  */
  const readNotes = extract.phase.status === 'succeeded' ? extract.phase.result.notes : ''

  /*
    A mail can carry several requisitions: two attached files, or one workbook whose deck and
    engine sheets go to different suppliers. The purchaser is asked rather than having them merged
    silently. Defaults to SPLIT — two that were really one order are easy to spot and merge,
    whereas a merge nobody was told about is invisible until a supplier is quoted for the wrong
    department.
  */
  // Body-read lines come from prose, which has no sheets — `groupLinesBySheet` yields one draft.
  const drafts = sourceItems.length > 0 ? groupLinesBySheet(sourceItems) : []
  const splittable = canSplit(drafts)
  /*
    Three modes, not two (Akhil, 2026-09-03) — see `draftsForMode`. `per-sheet` stays the default
    for the reason above: a wrong split is visible, a silent merge is not.
  */
  const [sheetMode, setSheetMode] = useState<SheetMode>('per-sheet')
  /** Which sheet, in `one-sheet` mode. Defaults to the first so the mode is never born empty. */
  const [chosenSheet, setChosenSheet] = useState<string | null>(null)
  const effectiveSheet = chosenSheet ?? drafts[0]?.label ?? null
  const plannedDrafts = draftsForMode(drafts, sheetMode, effectiveSheet)
  /** Lines the button will actually create — what the count on it must report. */
  const plannedLines = splittable ? plannedDrafts.reduce((n, d) => n + d.lines.length, 0) : lines.length

  /*
    ---- The enquiry type is now ASKED, not only guessed ----

    It used to come from the extraction alone and, failing that, default to `Technical` inside
    `buildCreateEnquiryWireBody`. That is not a cosmetic default: the type scopes item search for
    the whole enquiry (`items.get_items`'s `enquiry_type`), and Provision is the one type whose
    item-group mapping is actually populated (`D21`). A provision enquiry created as Technical
    searches a 93-item slice of the catalogue for the rest of its life.

    The extraction's guess is the DEFAULT, not the answer — `typeChoice` is `null` until the
    purchaser touches the control, so a better guess arriving later is still adopted.
  */
  const guessedType = extract.phase.status === 'succeeded' ? (extract.phase.result.enquiry_type ?? undefined) : undefined
  const [typeChoice, setTypeChoice] = useState<string | null>(null)
  const enquiryTypes = useEnquiryTypes()
  const enquiryType = typeChoice ?? guessedType

  /*
    Start matching the unmatched lines against the catalogue NOW, while the purchaser is still
    confirming the customer.

    The matcher takes ~16s per line, and until this existed that whole wait began when the RFQ
    page mounted — after the create round trip and the navigation. The line texts are known the
    moment the mail is read, so the wait can start here instead and be mostly over by arrival.

    Only lines with NO item code, because that is exactly what `RfqPage` asks for; prefetching a
    different set would key a different cache entry and buy nothing. Fire-and-forget by design —
    it must never delay or fail the create.
  */
  useEffect(() => {
    // Body-read lines get the same head start. They are the SLOWEST case to arrive at the RFQ
    // page — prose gives the matcher no item codes at all, so every line needs matching.
    const items = acceptedBodyLines ?? (extract.phase.status === 'succeeded' ? extract.phase.result.items : null)
    if (!items) return
    // The enquiry about to be created is the scope of its matches (2026-09-21).
    setItemScope({ company, enquiryType: enquiryType ?? '' })
    prefetchItemMatches(items.filter((i) => !i.item_code).map((i) => i.description))
    // Keyed on the phase object: a re-render with the same extraction must not re-fire it (the
    // query's own cache would dedupe it anyway, but this keeps the intent explicit).
  }, [extract.phase, acceptedBodyLines])

  if (!started) {
    return (
      <div style={{ borderTop: '1px solid var(--line)', marginTop: 14, paddingTop: 12 }}>
        <Button
          variant="primary"
          onClick={() => {
            setStarted(true)
            extract.mutate(mail)
          }}
        >
          ⚙️ Create enquiry from this mail
        </Button>
      </div>
    )
  }

  if (extract.isPending) {
    return (
      <div style={{ borderTop: '1px solid var(--line)', marginTop: 14, paddingTop: 12, color: 'var(--muted)' }}>
        Reading the mail…
      </div>
    )
  }

  /*
    A failed extraction USED to fall through to the normal bar: no message, no retry, and — because
    `lines` is `[]` for any non-succeeded phase — a Create that silently produced an enquiry with
    zero lines. The old panel surfaced this; the bar swallowed it. Today the primary path always
    fails (`B1`), so this is the common state, not an edge case.
  */
  // Once body lines are accepted the mail IS readable, so the failure banner must give way to
  // the normal bar rather than trapping the purchaser on an error they have already resolved.
  if (extract.phase.status === 'failed' && acceptedBodyLines === null) {
    return (
      <div style={{ borderTop: '1px solid var(--line)', marginTop: 14, paddingTop: 12 }}>
        <div className="warnflag" style={{ marginBottom: 8 }}>
          Could not read this mail: {extract.phase.message}
        </div>

        {/*
          The enquiry is often not IN the mail — it is behind a link in it. Seven of sixty inbound
          mails on this bench carry one (mostly a customer's Azure Blob export), and those mails
          look empty to the reader because nothing is attached.

          Offered here rather than fetched: the file is reachable, but it carries no
          `Access-Control-Allow-Origin`, so the browser will not read it — and fetching it through
          our own server would mean a proxy that retrieves whatever URL an inbound email names.
          See `mailFileLinks.ts`. The purchaser opens it, the browser downloads it, and the upload
          control on the RFQ builder reads the file with no endpoint at all.
        */}
        {fileLinks.length > 0 ? (
          <div style={{ marginBottom: 10, fontSize: 12.5 }}>
            <b>This mail links to {fileLinks.length === 1 ? 'a file' : `${fileLinks.length} files`}</b>{' '}
            <span style={{ color: 'var(--muted)' }}>
              — open it, then add it with “Upload doc / image” on the RFQ builder.
            </span>
            <div style={{ marginTop: 4 }}>
              {fileLinks.map((link) => (
                <div key={link.url}>
                  <a href={link.url} target="_blank" rel="noreferrer noopener" className="linkish">
                    ⬇ {link.label}
                  </a>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <Button onClick={() => extract.mutate(mail)}>↻ Try again</Button>{' '}
        {/*
          The rescue for a mail with no table and no numbered list — the case that produces this
          message most often. Primary, because "try again" re-runs the reader that has already
          failed on this mail and will fail again.
        */}
        <Button variant="primary" onClick={() => void readBody()}>
          ✨ Read the items from the text
        </Button>

        {readingBody ? (
          <ReadBodyModal
            lines={bodyLines}
            pending={bodyPending}
            error={bodyError}
            onRetry={() => void readBody()}
            onClose={() => setReadingBody(false)}
            onConfirm={(reviewed) => {
              setReadingBody(false)
              /*
                Handed to the normal bar, NOT created here — see `acceptedBodyLines`.

                Creating from inside this dialog skipped the customer and company pickers, which
                only exist on the bar below, and sent whatever `customer` had been guessed from
                the sender. For a mail matching no known customer that is `''`, and the server
                answered "Value missing for Enquiry: Customer".

                `toExtractedLine` first, so a body's raw unit ("boxes") travels the same
                unresolved-UOM route the spreadsheet path uses instead of failing the document.
              */
              setAcceptedBodyLines(reviewed.map(toExtractedLine))
            }}
          />
        ) : null}
      </div>
    )
  }

  return (
    <div style={{ borderTop: '1px solid var(--line)', marginTop: 14, paddingTop: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          Creating enquiry for <b>{customer || '—'}</b> · deliver to <b>{effectiveLocation}</b> · as{' '}
          <b>{company || '— pick a company'}</b>
        </span>
        {/* Provenance, because these lines were READ FROM PROSE rather than from a document with
            columns — the one source on this screen with no structure behind it. It survives to the
            bar so the purchaser is not asked to trust an assistant's reading silently. */}
        {acceptedBodyLines !== null ? (
          <Badge tone="warn" title="Read from the mail text by the assistant, then reviewed by you">
            {acceptedBodyLines.length} line{acceptedBodyLines.length === 1 ? '' : 's'} read from the text
          </Badge>
        ) : null}
        <button className="linkish" onClick={() => setExpandedByUser((v) => !(v ?? autoExpand))}>
          {expanded ? 'Hide' : 'Change'}
        </button>
        <span style={{ flex: 1 }} />
        <Button
          variant="primary"
          // `plannedLines === 0` covers the one reachable dead end: `one-sheet` with a selection
          // that matches nothing. Creating an empty enquiry from a mail that plainly had lines in
          // it is the wrong answer to that.
          disabled={customer === '' || company === '' || create.isPending || plannedLines === 0}
          onClick={() => {
            const header = {
              source: 'mail' as const,
              mail_id: mail.id,
              customer,
              company,
              delivery_location: effectiveLocation,
              // The location's selling price list, written with it (2026-09-17).
              price_list: effectiveLocation ? (priceLists.data?.get(effectiveLocation) ?? null) : null,
              enquiry_type: enquiryType,
              // Omitted entirely when we could not read the signed-in user: `purchaser` is
              // optional server-side (a separate `assign_purchaser` call, skipped when absent),
              // and sending an unresolvable name fails the whole create.
              purchaser: currentUser.data?.email ?? undefined,
            }

            /*
              One enquiry per sheet, when the purchaser chose to split.

              Sequential, NOT Promise.all: each create is a separate document on the server, and
              firing them together means a failure halfway leaves an unknown number created with
              nothing saying which. One at a time, the mutation's own error handling reports the
              first failure and the rest are simply not attempted.

              Every enquiry carries the SAME header — customer, company, delivery location. The
              sheet name goes in the line note rather than the enquiry type: a sheet called
              "Engine" usually means Technical, and usually is not a promise.
            */
            if (splittable) {
              /*
                One create per planned draft, STRICTLY IN SEQUENCE.

                The comment above has always said "one at a time … the rest are simply not
                attempted" — but `.mutate()` in a `for` loop fires them all at once, so the exact
                failure it describes was live: a failure halfway left an unknown number created
                with nothing saying which. `runSerially` is what makes the comment true.
              */
              const created: string[] = []
              void runSerially(
                plannedDrafts.map((draft) => async () => {
                  const { enquiry } = await create.mutateAsync({ ...header, items: draft.lines })
                  created.push(enquiry.id)
                }),
              ).then(({ firstError }) => {
                if (firstError) {
                  toast(
                    created.length === 0
                      ? `Nothing was created: ${firstError.message}`
                      : `Created ${created.join(', ')}, then stopped: ${firstError.message}`,
                  )
                } else {
                  toast(`Created ${created.length} enquiries: ${created.join(', ')}`)
                }
                // Once, to the first — the others are on the board. Navigating per create sent the
                // purchaser through screens they never asked for.
                if (created[0]) navigate(`/rfq/${created[0]}`)
              })
              return
            }
            void create
              .mutateAsync({ ...header, items: lines })
              .then(({ enquiry, warning }) => {
                // A follow-up failure is the half worth saying out loud — see `createEnquiryFlow`.
                toast(warning ?? `Enquiry ${enquiry.id} created`)
                navigate(`/rfq/${enquiry.id}`)
              })
              .catch(() => {
                /* reported by the mutation's own onError */
              })
          }}
        >
          {/* Says what the choice above will actually do — the counts change with the mode, and a
              bare "Create" leaves the purchaser to work out whether they are about to make one
              enquiry or three. */}
          {create.isPending
            ? 'Creating…'
            : splittable && plannedDrafts.length > 1
              ? `Create ${plannedDrafts.length} enquiries`
              : `Create${splittable ? ` · ${plannedLines} line${plannedLines === 1 ? '' : 's'}` : ''}`}
        </Button>
      </div>

      {splittable ? (
        <div style={{ marginTop: 8, fontSize: 12.5 }}>
          <div style={{ color: 'var(--muted)', marginBottom: 4 }}>
            This mail holds <b>{drafts.length} separate requisitions</b>:{' '}
            {drafts.map((d) => `${d.label} (${d.lines.length})`).join(' · ')}
          </div>
          <div style={{ display: 'grid', gap: 3 }}>
            <label>
              <input
                type="radio"
                checked={sheetMode === 'per-sheet'}
                onChange={() => setSheetMode('per-sheet')}
              />{' '}
              Create <b>{drafts.length} enquiries</b>, one per sheet
            </label>
            <label>
              <input type="radio" checked={sheetMode === 'combined'} onChange={() => setSheetMode('combined')} />{' '}
              Create <b>one enquiry</b> combining all {lines.length} lines
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>
                <input
                  type="radio"
                  checked={sheetMode === 'one-sheet'}
                  onChange={() => setSheetMode('one-sheet')}
                />{' '}
                Create <b>one enquiry</b> from just
              </span>
              {/*
                The picker is live in every mode, not only the selected one: clicking straight onto
                the sheet you want is one action, and having to select the radio first before the
                dropdown wakes up is two. Choosing a sheet selects the mode.
              */}
              <select
                value={effectiveSheet ?? ''}
                onChange={(e) => {
                  setChosenSheet(e.target.value)
                  setSheetMode('one-sheet')
                }}
              >
                {drafts.map((d) => (
                  <option key={d.label} value={d.label}>
                    {d.label} ({d.lines.length} line{d.lines.length === 1 ? '' : 's'})
                  </option>
                ))}
              </select>
              <span style={{ color: 'var(--muted)' }}>
                — the rest is a price list or a covering note
              </span>
            </label>
          </div>
        </div>
      ) : null}

      {/*
        ALREADY PROCESSED — said before the button, not after.

        13 mails on this bench carry more than one enquiry and one carries TWENTY-FOUR. Some of
        that is the deliberate multi-sheet split; the rest is somebody pressing Create again with
        nothing on screen to tell them it had already been done. Creating a second enquiry is still
        allowed — it is a legitimate thing to want, and a mail genuinely can hold two requisitions —
        but it should now be a decision rather than an accident.
      */}
      {alreadyProcessed.length > 0 ? (
        <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--warn)' }}>
          ⚠ This mail has already been turned into{' '}
          <b>
            {alreadyProcessed.length === 1
              ? 'an enquiry'
              : `${alreadyProcessed.length} enquiries`}
          </b>
          :{' '}
          {alreadyProcessed.map((e, i) => (
            <span key={e.id}>
              {i > 0 ? ', ' : null}
              <span className="linkish" onClick={() => navigate(`/rfq/${e.id}`)}>
                {e.id}
              </span>
            </span>
          ))}
          . Creating another will add a separate one.
        </div>
      ) : null}

      {/* Shown whenever a file was skipped — `Not read:` is the half that matters, and it is the
          half nobody could see. */}
      {readNotes && readNotes.includes('Not read:') ? (
        <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--warn)' }}>⚠ {readNotes}</div>
      ) : readNotes ? (
        <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--muted)' }}>{readNotes}</div>
      ) : null}

      {allOurs ? (
        <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--warn)' }}>
          That letterhead looks like <b>your own company</b> — pick the buyer.
        </div>
      ) : null}

      {!guessedLocation.matched ? (
        <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--warn)' }}>
          The mail names no delivery location we recognise — <b>{effectiveLocation}</b> is a default. Check it.
        </div>
      ) : null}

      {expanded ? (
        <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          {/* Company first (Akhil, 2026-09-22): the customer search is scoped to the chosen
              company, so it is picked last, once the company is known. */}
          <label className="ef">
            <span>Company</span>
            <LinkCombo
              value={company}
              onChange={chooseCompany}
              useOptions={useCompanyPicker}
              placeholder="Pick the operating company…"
            />
            <div style={{ fontSize: 11, marginTop: 4, color: 'var(--muted)' }}>
              {/* Which entity raises this enquiry decides which entity's name is on the RFQs and
                  the quotation, and which currency they are in. Nothing in the mail says it. */}
              {company === ''
                ? 'Required — this decides whose name the RFQs go out under.'
                : 'Remembered for this session.'}
            </div>
          </label>
          {/*
            Point: ask for the type instead of inheriting a guess.

            A plain select over the SERVER's `Enquiry Type` master, not our `ENQUIRY_TYPES` const —
            the two disagree (the server has `Service`, and has neither `Commercial` nor `Bonded
            Stores`), and offering a value the server will reject is worse than offering none.

            The hint says what the choice DOES, because its consequence is invisible otherwise:
            the type scopes item search for the whole enquiry.
          */}
          <label className="ef">
            <span>Enquiry type</span>
            <select
              value={enquiryType ?? ''}
              onChange={(e) => setTypeChoice(e.target.value)}
              disabled={enquiryTypes.isLoading}
            >
              {/* Only until the purchaser or the extraction supplies one — never a value to save. */}
              {enquiryType ? null : <option value="">Choose…</option>}
              {(enquiryTypes.data ?? []).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
              {/* The extraction can guess a type the master does not list; keep it selectable
                  rather than silently switching the purchaser to something else. */}
              {enquiryType && !(enquiryTypes.data ?? []).includes(enquiryType) ? (
                <option value={enquiryType}>{enquiryType}</option>
              ) : null}
            </select>
            <div style={{ fontSize: 11, marginTop: 3, color: 'var(--muted)' }}>
              {typeChoice === null && guessedType ? 'Read from the mail — change it if wrong. ' : null}
              Scopes item search on the RFQ page.
            </div>
          </label>

          <label className="ef">
            <span>Delivery location</span>
            <LinkCombo
              value={effectiveLocation}
              onChange={setLocation}
              useOptions={useDeliveryLocationPicker}
              placeholder="Search locations…"
            />
          </label>
          <label className="ef">
            <span>Customer</span>
            <LinkCombo
              value={customer}
              onChange={setChosen}
              useOptions={useScopedCustomers}
              placeholder="Search customers…"
            />
            {/* The resolver often returns several near-identical customer records. Showing them
                saves retyping a search that already ran — `LinkCombo` can only search the server,
                so without this the alternatives it found are simply discarded. */}
            {candidates.length > 1 ? (
              <div style={{ fontSize: 11, marginTop: 4 }}>
                <span style={{ color: 'var(--muted)' }}>also matched: </span>
                {candidates.slice(1).map((c) => (
                  <button key={c} className="linkish" style={{ marginRight: 8 }} onClick={() => setChosen(c)}>
                    {c}
                  </button>
                ))}
              </div>
            ) : null}
          </label>
        </div>
      ) : null}
    </div>
  )
}
